<template>
  <div>
    <div>
      <v-breadcrumbs
        class="px-0"
        :items="breadcrumbItems"
      />
    </div>
    <page-title :value="pageTitle" />

    <v-progress-linear v-if="isFetching" indeterminate color="primary" />

    <div v-if="detail" class="my-4">
      <div class="d-flex align-center flex-wrap" style="gap: 1rem;">
        <span class="text-h6 font-weight-bold">
          {{ detail.lastName }} {{ detail.firstName }}
        </span>
        <span class="text-caption text-grey">
          {{ detail.className }} / {{ detail.ageLabel }}
        </span>
        <ai-report-status-chip :status="detail.status" />
      </div>

      <div class="d-flex align-center flex-wrap mt-2 text-body-2 text-grey" style="gap: 1rem;">
        <span>{{ $t('aiReport.period', { months: detail.monthsLabel }) }}</span>
        <span :class="{ 'text-red': detail.dataCoverageMonths.length < 3 }">
          {{
            $t('aiReport.coverageMonths', {
              months: formatCoverage(detail.dataCoverageMonths),
            })
          }}
          <span v-if="detail.dataCoverageMonths.length < 3">
            {{ $t('aiReport.coveragePartial') }}
          </span>
        </span>
        <span>{{ $t('aiReport.generationsCount', { count: detail.generationCount }) }}</span>
      </div>

      <v-alert
        v-if="isRegenerating && !regenerateDialogOpen"
        variant="outlined"
        color="primary"
        icon="mdi-progress-clock"
        density="compact"
        class="mt-3"
      >
        <div class="d-flex align-center flex-wrap" style="gap: 1rem;">
          <span class="text-body-2">
            {{
              $t('aiReport.generatingProgressShort', {
                action: $t('aiReport.regenerating'),
                success: activeBatch ? activeBatch.successCount : 0,
                total: activeBatch ? activeBatch.totalCount : 0,
              })
            }}
          </span>
          <v-progress-linear
            indeterminate
            color="primary"
            height="6"
            rounded
            style="max-width: 240px;"
          />
        </div>
      </v-alert>

      <v-row class="mt-4">
        <v-col cols="12" md="8">
          <v-card variant="outlined" class="pa-5">
            <div class="d-flex align-center mb-3">
              <img
                src="@/assets/vevo/mascot_peek.png"
                alt="VEVO"
                class="mr-2"
                style="width: 28px; height: 28px; object-fit: contain;"
              />
              <span class="text-subtitle-1 font-weight-bold">{{ $t('aiReport.reportBody') }}</span>
              <v-spacer />
              <span class="text-caption text-grey" v-if="detail.content">
                {{ $t('aiReport.characters', { count: contentLength }) }}
              </span>
            </div>

            <div v-if="detail.content" class="ai-report-content text-body-1">
              {{ detail.content }}
            </div>
            <v-alert
              v-else
              type="info"
              variant="outlined"
              density="compact"
            >
              {{ $t('aiReport.noReportYet') }}
            </v-alert>

            <v-divider class="my-4" />

            <div class="d-flex align-center flex-wrap" style="gap: 0.5rem;">
              <v-btn
                v-if="detail.status === 'draft'"
                color="primary"
                variant="flat"
                :loading="isPublishing"
                @click="setStatus('published')"
              >
                <v-icon start>mdi-publish</v-icon>
                {{ $t('aiReport.publish') }}
              </v-btn>
              <v-btn
                v-if="detail.status === 'published'"
                variant="outlined"
                :loading="isPublishing"
                @click="setStatus('draft')"
              >
                <v-icon start>mdi-undo</v-icon>
                {{ $t('aiReport.revertToDraft') }}
              </v-btn>
              <v-tooltip
                v-if="detail.status === 'draft'"
                :disabled="!noData"
                location="top"
                :text="$t('aiReport.cannotGenerateNoData')"
              >
                <template v-slot:activator="{ props }">
                  <span v-bind="props">
                    <v-btn
                      variant="outlined"
                      :disabled="noData || isRegenerating"
                      @click="regenerateDialogOpen = true"
                    >
                      <v-icon start>mdi-refresh</v-icon>
                      {{ $t('aiReport.regenerate') }}
                    </v-btn>
                  </span>
                </template>
              </v-tooltip>
              <v-tooltip
                v-if="detail.status === 'notGenerated'"
                :disabled="!noData"
                location="top"
                :text="$t('aiReport.cannotGenerateNoData')"
              >
                <template v-slot:activator="{ props }">
                  <span v-bind="props">
                    <v-btn
                      color="primary"
                      variant="outlined"
                      :disabled="noData || isRegenerating"
                      @click="regenerateDialogOpen = true"
                    >
                      <v-icon start>mdi-auto-fix</v-icon>
                      {{ $t('aiReport.generate') }}
                    </v-btn>
                  </span>
                </template>
              </v-tooltip>
            </div>

            <v-alert
              v-if="detail.status === 'published'"
              density="compact"
              variant="outlined"
              type="warning"
              class="mt-3 text-body-2"
            >
              {{ $t('aiReport.publishedSkipHint') }}
            </v-alert>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card variant="outlined" class="pa-5">
            <div class="text-subtitle-2 font-weight-bold text-grey-darken-2 mb-2">
              {{ $t('aiReport.activityLog') }}
            </div>
            <div class="text-body-2 mb-1">
              <span class="text-grey">{{ $t('aiReport.lastGenerated') }}</span>
              {{ formatDateTime(detail.lastGeneratedAt) }}
              <span v-if="detail.lastGeneratedBy" class="text-grey">
                / by {{ detail.lastGeneratedBy }}
              </span>
            </div>
            <div class="text-body-2 mb-1">
              <span class="text-grey">{{ $t('aiReport.lastPublished') }}</span>
              {{ formatDateTime(detail.lastPublishedAt) }}
              <span v-if="detail.lastPublishedBy" class="text-grey">
                / by {{ detail.lastPublishedBy }}
              </span>
            </div>
            <v-divider class="my-3" />
            <div class="text-subtitle-2 font-weight-bold text-grey-darken-2 mb-2">
              {{ $t('aiReport.latestRegenerate') }}
            </div>
            <div v-if="detail.additionalPrompt" class="text-body-2">
              {{ detail.additionalPrompt }}
            </div>
            <div v-else class="text-body-2 text-grey">
              {{ $t('aiReport.noTranslation') }}
            </div>
          </v-card>

          <v-card variant="outlined" class="pa-4 mt-3">
            <div class="text-subtitle-2 font-weight-bold text-grey-darken-2 mb-2">
              {{ $t('aiReport.statusGuide') }}
            </div>
            <div class="d-flex flex-column" style="gap: 0.5rem;">
              <div class="d-flex align-center" style="gap: 0.5rem;">
                <ai-report-status-chip status="notGenerated" />
                <span class="text-body-2">{{ $t('aiReport.statusNotGeneratedHint') }}</span>
              </div>
              <div class="d-flex align-center" style="gap: 0.5rem;">
                <ai-report-status-chip status="draft" />
                <span class="text-body-2">{{ $t('aiReport.statusDraftHint') }}</span>
              </div>
              <div class="d-flex align-center" style="gap: 0.5rem;">
                <ai-report-status-chip status="published" />
                <span class="text-body-2">{{ $t('aiReport.statusPublishedHint') }}</span>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <v-dialog v-model="regenerateDialogOpen" max-width="540" persistent>
      <v-card class="pa-5">
        <page-title :value="regenerateDialogTitle" />

        <p class="mt-3 text-body-2">
          {{ regenerateIntro }}
        </p>

        <v-alert variant="outlined" color="primary" icon="mdi-information-outline" density="compact" class="mt-2">
          <ul class="ma-0 pl-4 text-body-2">
            <li v-if="regenerateMode === 'translate'">
              {{ $t('aiReport.regenerateHints.translate') }}
            </li>
            <li>{{ $t('aiReport.regenerateHints.async') }}</li>
            <li>{{ $t('aiReport.regenerateHints.backToDraft') }}</li>
          </ul>
        </v-alert>

        <template v-if="canTranslate">
          <v-radio-group
            v-model="regenerateMode"
            class="mt-3"
            density="compact"
            hide-details
            :disabled="!!activeBatch && activeBatch.status === 'processing'"
          >
            <v-radio :label="$t('aiReport.modeSimple')" value="simple" />
            <v-radio :label="$t('aiReport.modeTranslate')" value="translate" />
          </v-radio-group>

          <v-select
            v-if="regenerateMode === 'translate'"
            v-model="selectedLanguage"
            :items="languageItems"
            item-title="label"
            item-value="code"
            :label="$t('aiReport.targetLanguage')"
            variant="outlined"
            density="compact"
            class="mt-3"
            :disabled="!!activeBatch && activeBatch.status === 'processing'"
          />
        </template>

        <div v-if="activeBatch" class="mt-2">
          <v-progress-linear
            :model-value="batchProgressPercent"
            :indeterminate="activeBatch.status === 'processing' && activeBatch.successCount === 0"
            color="primary"
            height="10"
            rounded
          />
          <p class="mt-2 text-body-2">
            <template v-if="activeBatch.status === 'processing'">
              {{
                $t('aiReport.generatingProgressShort', {
                  action:
                    detail && detail.status === 'notGenerated'
                      ? $t('aiReport.generating')
                      : $t('aiReport.regenerating'),
                  success: activeBatch.successCount,
                  total: activeBatch.totalCount,
                })
              }}
            </template>
            <template v-else-if="activeBatch.status === 'completed'">
              <v-icon size="small" color="success">mdi-check-circle</v-icon>
              {{
                detail && detail.status === 'notGenerated'
                  ? $t('aiReport.generationCompleted')
                  : $t('aiReport.regenerateCompleted')
              }}
            </template>
            <template v-else-if="activeBatch.status === 'failed'">
              <v-icon size="small" color="red">mdi-alert-circle</v-icon>
              {{
                detail && detail.status === 'notGenerated'
                  ? $t('aiReport.generationFailed')
                  : $t('aiReport.regenerateFailed')
              }}
            </template>
          </p>
        </div>

        <div class="d-flex mt-4" style="gap: 0.5rem;">
          <v-spacer />
          <v-btn variant="text" @click="closeRegenerateDialog" :disabled="isStartingRegenerate">
            {{
              activeBatch && activeBatch.status === 'completed'
                ? $t('common.close')
                : $t('common.cancel')
            }}
          </v-btn>
          <v-btn
            v-if="!activeBatch || activeBatch.status === 'failed'"
            color="primary"
            variant="flat"
            :loading="isStartingRegenerate"
            :disabled="startRegenerateDisabled"
            @click="startRegenerate"
          >
            {{ startButtonLabel }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import dayjs from "dayjs";
import PageTitle from "@/components/atoms/PageTitle.vue";
import AiReportStatusChip from "@/components/molecules/AiReportStatusChip.vue";
import { aiReportDetailModule } from "@/store/viewModules/ai-report/aiReportDetailModule";
import { AI_REPORT_LANGUAGES } from "@/api/ApiAiReport";
import type { AiReportLanguage, AiReportQuarter } from "@/api/ApiAiReport";

type RegenerateMode = "simple" | "translate";

interface DataType {
  year: number;
  quarter: AiReportQuarter;
  childId: number;
  regenerateDialogOpen: boolean;
  regenerateMode: RegenerateMode;
  selectedLanguage: AiReportLanguage | null;
  isStartingRegenerate: boolean;
  isPublishing: boolean;
  pollTimer: number | null;
}

export default defineComponent({
  components: {
    PageTitle,
    AiReportStatusChip,
  },
  data: (): DataType => ({
    year: 0,
    quarter: 1 as AiReportQuarter,
    childId: 0,
    regenerateDialogOpen: false,
    regenerateMode: "simple",
    selectedLanguage: null,
    isStartingRegenerate: false,
    isPublishing: false,
    pollTimer: null,
  }),
  computed: {
    detail() {
      return aiReportDetailModule.detail;
    },
    isFetching(): boolean {
      return aiReportDetailModule.isFetching;
    },
    activeBatch() {
      return aiReportDetailModule.activeBatch;
    },
    pageTitle(): string {
      return this.$t("aiReport.titleIndividual") as string;
    },
    breadcrumbItems(): Array<{ title: string; href?: string; color?: string }> {
      const name = this.detail
        ? `${this.detail.lastName} ${this.detail.firstName}`
        : (this.$t("aiReport.childFallback") as string);
      const monthsLabel = this.detail
        ? this.detail.monthsLabel
        : (this.$t("aiReport.quarterLabel", { quarter: this.quarter }) as string);
      return [
        {
          title: this.$t("aiReport.title") as string,
          href: "/ai-report",
          color: "primary",
        },
        {
          title: this.$t("aiReport.schoolYearLabel", {
            year: this.year,
            months: monthsLabel,
          }) as string,
          href: `/ai-report/quarter/${this.year}/${this.quarter}`,
          color: "primary",
        },
        { title: name },
      ];
    },
    canRegenerate(): boolean {
      if (!this.detail) return false;
      return this.detail.status !== "published";
    },
    noData(): boolean {
      return !!this.detail && this.detail.dataCoverageMonths.length === 0;
    },
    isRegenerating(): boolean {
      return !!this.activeBatch && this.activeBatch.status === "processing";
    },
    contentLength(): number {
      return this.detail && this.detail.content ? this.detail.content.length : 0;
    },
    regenerateDialogTitle(): string {
      if (!this.detail) return this.$t("aiReport.generateTitle") as string;
      return this.detail.status === "notGenerated"
        ? (this.$t("aiReport.generateTitle") as string)
        : (this.$t("aiReport.regenerateTitle") as string);
    },
    regenerateIntro(): string {
      const name = this.detail
        ? `${this.detail.lastName} ${this.detail.firstName}`
        : "";
      if (this.detail && this.detail.status === "notGenerated") {
        return this.$t("aiReport.regenerateIntroGenerate", { name }) as string;
      }
      return this.$t("aiReport.regenerateIntroRegenerate", { name }) as string;
    },
    batchProgressPercent(): number {
      const b = this.activeBatch;
      if (!b || b.totalCount === 0) return 0;
      return Math.floor((b.successCount / b.totalCount) * 100);
    },
    languageItems() {
      return AI_REPORT_LANGUAGES;
    },
    canTranslate(): boolean {
      return !!this.detail && this.detail.status === "draft" && !!this.detail.content;
    },
    startRegenerateDisabled(): boolean {
      return this.regenerateMode === "translate" && this.selectedLanguage == null;
    },
    startButtonLabel(): string {
      if (this.regenerateMode === "translate") {
        return this.$t("aiReport.startTranslation") as string;
      }
      return this.detail && this.detail.status === "notGenerated"
        ? (this.$t("aiReport.startGenerate") as string)
        : (this.$t("aiReport.startRegenerate") as string);
    },
  },
  watch: {
    "$route.params": {
      handler() {
        this.readRouteParams();
        this.loadDetail();
      },
    },
  },
  beforeUnmount() {
    this.stopPolling();
  },
  async mounted() {
    this.readRouteParams();
    await this.loadDetail();
    await this.resumeRegenerateIfRunning();
  },
  methods: {
    readRouteParams() {
      this.year = parseInt(this.$route.params.businessYear as string, 10);
      this.quarter = parseInt(this.$route.params.quarter as string, 10) as AiReportQuarter;
      this.childId = parseInt(this.$route.params.childId as string, 10);
    },
    regenBatchKey(): string {
      return `ai_report_regen_batch_${this.childId}_${this.year}_${this.quarter}`;
    },
    saveRegenBatchId(batchId: number): void {
      try {
        window.localStorage.setItem(this.regenBatchKey(), String(batchId));
      } catch (e) {
        /* continue without restore if localStorage unavailable */
      }
    },
    readRegenBatchId(): number | null {
      try {
        const v = window.localStorage.getItem(this.regenBatchKey());
        if (!v) return null;
        const n = parseInt(v, 10);
        return Number.isNaN(n) ? null : n;
      } catch (e) {
        return null;
      }
    },
    clearRegenBatchId(): void {
      try {
        window.localStorage.removeItem(this.regenBatchKey());
      } catch (e) {
        /* noop */
      }
    },
    async resumeRegenerateIfRunning() {
      const batchId = this.readRegenBatchId();
      if (batchId == null) return;
      try {
        const batch = await aiReportDetailModule.pollBatch({ batchId });
        if (batch && batch.status === "processing") {
          this.startPolling(batchId);
        } else {
          this.clearRegenBatchId();
          aiReportDetailModule.setActiveBatch(null);
        }
      } catch (e) {
        /* keep localStorage for next retry */
      }
    },
    async loadDetail() {
      if (!this.year || !this.quarter || !this.childId) return;
      await aiReportDetailModule.loadDetail({
        businessYear: this.year,
        quarter: this.quarter,
        childId: this.childId,
      });
    },
    async setStatus(status: "published" | "draft") {
      if (!this.detail) return;
      const name = `${this.detail.lastName} ${this.detail.firstName}`;
      const message =
        status === "published"
          ? (this.$t("aiReport.confirmPublish", { name }) as string)
          : (this.$t("aiReport.confirmRevertDraft", { name }) as string);
      const ok = window.confirm(message);
      if (!ok) return;
      this.isPublishing = true;
      try {
        await aiReportDetailModule.setStatus({
          businessYear: this.year,
          quarter: this.quarter,
          childId: this.childId,
          status,
        });
      } finally {
        this.isPublishing = false;
      }
    },
    async startRegenerate() {
      this.isStartingRegenerate = true;
      aiReportDetailModule.setActiveBatch(null);
      try {
        const language =
          this.regenerateMode === "translate" ? this.selectedLanguage : null;
        const batch = await aiReportDetailModule.regenerate({
          businessYear: this.year,
          quarter: this.quarter,
          childId: this.childId,
          language,
        });
        if (batch) {
          this.saveRegenBatchId(batch.batchId);
          this.startPolling(batch.batchId);
        }
      } finally {
        this.isStartingRegenerate = false;
      }
    },
    startPolling(batchId: number) {
      this.stopPolling();
      this.pollTimer = window.setInterval(async () => {
        const batch = await aiReportDetailModule.pollBatch({ batchId });
        if (batch && (batch.status === "completed" || batch.status === "failed")) {
          this.stopPolling();
          this.clearRegenBatchId();
          await this.loadDetail();
        }
      }, 1500);
    },
    stopPolling() {
      if (this.pollTimer !== null) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    closeRegenerateDialog() {
      this.regenerateDialogOpen = false;
      this.stopPolling();
      this.regenerateMode = "simple";
      this.selectedLanguage = null;
      aiReportDetailModule.setActiveBatch(null);
    },
    formatDateTime(value: string | null): string {
      if (!value) return "—";
      return dayjs(value).format("DD/MM/YYYY HH:mm");
    },
    formatCoverage(months: number[]): string {
      if (months.length === 0) return this.$t("aiReport.coverageNone") as string;
      return months.join(", ");
    },
  },
});
</script>

<style lang="scss" scoped>
.ai-report-content {
  white-space: pre-wrap;
  line-height: 1.8;
  font-feature-settings: "palt";
}
</style>
